import { useCallback } from 'react';

import emptyPng from '@assets/empty.png';
import { EFavoriteType, favoriteTypeEnumToText } from '@domain/Customer';
import { cn } from '@lib/ui/cn';
import CustomerService from '@services/customers/CustomerService';
import type { IListFavoritesOutputDTO } from '@services/customers/dtos/listFavorites/IOutputData';
import type { IRemoveMedicationToFavoriteInputDTO } from '@services/medications/dtos/removeToFavorites/IInputData';
import { medicationService } from '@services/medications/MedicationService';
import type { IRemoveTreatmentToFavoriteInputDTO } from '@services/treatments/dtos/removeToFavorites/IInputData';
import { treatmentService } from '@services/treatments/TreatmentService';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { LoaderCircle, Trash } from 'lucide-react';
import { Link } from 'react-router';

function FavoritesList() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery<IListFavoritesOutputDTO>({
    queryFn: async () => {
      const response = await CustomerService.listFavorites();
      return response;
    },
    queryKey: ['favorites'],
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingDrug,
    mutateAsync: mutateAsyncDrug,
    variables: variablesDrug,
  } = useMutation<void, unknown, IRemoveMedicationToFavoriteInputDTO>({
    mutationFn: medicationService.removeToFavorites,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const {
    isPending: isPendingTheme,
    mutateAsync: mutateAsyncTheme,
    variables: variablesTheme,
  } = useMutation<void, unknown, IRemoveTreatmentToFavoriteInputDTO>({
    mutationFn: treatmentService.removeToFavorites,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const handleFavorite = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      id: string,
      type: EFavoriteType,
    ) => {
      event.stopPropagation();
      event.preventDefault();
      if (type === EFavoriteType.MEDICATION) {
        await mutateAsyncDrug({ id });
      }
      if (type === EFavoriteType.TREATMENT) {
        await mutateAsyncTheme({ id });
      }
    },
    [mutateAsyncDrug, mutateAsyncTheme],
  );

  return (
    <div>
      {data.list.length ? (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          {data.list.map((item) => (
            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all hover:scale-[1.02] lg:p-4 lg:text-lg',
              )}
              key={item.id}
              to={
                item.type === EFavoriteType.MEDICATION
                  ? `/medicamentos/${item.id}`
                  : `/temas/1/topicos/${item.id}`
              }>
              <div className="flex flex-col gap-2">
                <span>{item.title}</span>
                <span className="text-tertiary text-sm">
                  {favoriteTypeEnumToText(item.type)}
                </span>
              </div>

              {(isPendingDrug && variablesDrug.id === item.id) ||
              (isPendingTheme && variablesTheme.id === item.id) ? (
                <LoaderCircle className="text-tertiary size-8 animate-spin" />
              ) : (
                <button
                  className={cn(
                    '[&>svg]:text-tertiary [&>svg]:fill-tertiary hover:[&>svg]:fill-tertiary/50 cursor-pointer transition-all [&>svg]:size-8',
                  )}
                  onClick={(e) => {
                    handleFavorite(e, item.id, item.type);
                  }}>
                  <Trash />
                </button>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-36 flex flex-col justify-center">
          <img
            alt="Imagem de listagem vazia"
            className="mx-auto max-w-80"
            src={emptyPng}
          />
          <p className="mt-4 text-center text-lg font-bold">
            Não há registros!
          </p>
          <p className="mt-4 text-center text-lg">
            Não encontramos anotações favoritadas por você
          </p>
        </div>
      )}
    </div>
  );
}

export default FavoritesList;
